import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { CheckCircle2, GraduationCap, Rocket, Shield } from "lucide-react";

export const Route = createFileRoute("/revenda-iptv/iniciantes")({
  head: () => ({
    meta: [
      { title: "Revenda IPTV para Iniciantes: Guia do Zero ao Lucro | P2 PRO" },
      { name: "description", content: "Aprenda como começar sua revenda IPTV hoje, mesmo sendo um iniciante. Guia passo a passo com investimento baixo, suporte VIP e conteúdo educativo completo." },
      { name: "keywords", content: "revenda iptv iniciantes, curso revenda iptv, como revender streaming, ganhar dinheiro com iptv do zero" },
    ],
  }),
  component: () => (
    <SEOPage
      variant="default"
      showTestimonials={true}
      category="Educação para Iniciantes"
      title="REVENDA IPTV PARA INICIANTES: O PASSO A PASSO"
      subtitle="Não sabe por onde começar? Criamos o trilho do sucesso para você realizar sua primeira venda ainda hoje."
      introParagraphs={[
        "Muitas pessoas olham para o mercado de streaming e acreditam que precisam ser técnicos de TI ou experts em redes para lucrar. A grande verdade é que a <a href='/' className='text-electric hover:underline font-bold'>Revenda IPTV da P2 PRO</a> foi desenhada justamente para quem está começando do absoluto zero. Nossa missão é democratizar o empreendedorismo digital, fornecendo a tecnologia pesada para que você se preocupe apenas com o relacionamento e as vendas.",
        "Ao se tornar um parceiro iniciante, você não recebe apenas um login e senha; você ganha acesso a uma mentoria completa. Ensinamos desde os termos básicos (o que é DNS, M3U, Xtream Codes) até as estratégias de abordagem no WhatsApp que realmente funcionam. O modelo pós-pago é o seu maior aliado nessa fase, pois permite que você valide o mercado sem precisar investir fortunas em estoque parado.",
        "Neste guia especial para novos parceiros, vamos desmistificar o funcionamento do negócio e provar que, com dedicação e o suporte correto, qualquer pessoa com um smartphone pode construir uma renda mensal robusta e recorrente em poucos meses."
      ]}
      sections={[
        {
          title: "Sua Jornada de Aprendizado",
          type: "grid",
          content: (
            <>
              {[
                { title: "Fase 1: O Despertar", desc: "Entenda a oportunidade do mercado de streaming e como a P2 PRO garante sua estabilidade.", icon: <GraduationCap className="text-electric" /> },
                { title: "Fase 2: A Decolagem", desc: "Realize suas primeiras ativações usando o gerador de testes gratuito ilimitado.", icon: <Rocket className="text-orange-400" /> },
                { title: "Fase 3: A Blindagem", desc: "Aprenda a fidelizar seus clientes e garantir que eles renovem todos os meses sem falha.", icon: <Shield className="text-green-400" /> }
              ].map((item, i) => (
                <div key={i} className="p-8 rounded-3xl bg-white/[0.02] border border-white/5 hover:border-electric/30 transition-all text-center">
                  <div className="flex justify-center mb-4 text-3xl">{item.icon}</div>
                  <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </>
          )
        },
        {
          title: "Mentoria Exclusiva para Novos Parceiros",
          type: "featured",
          content: (
            <div className="grid lg:grid-cols-2 gap-12 items-center text-white">
              <div>
                <h3 className="text-2xl font-black mb-6 italic uppercase italic">Você nunca estará sozinho</h3>
                <p className="text-white/80 leading-relaxed mb-8">
                  Sabemos que as primeiras dúvidas podem ser intimidantes. Por isso, oferecemos um canal de suporte humanizado que atende você em tempo real. Se o seu cliente não souber instalar o app na Smart TV, nós ensinamos você a ensinar ele.
                </p>
                <div className="space-y-4">
                  {[
                    "Treinamento básico em vídeo (Painel e Apps)",
                    "Scripts de atendimento prontos para copiar",
                    "Acesso a grupo de networking com veteranos",
                    "Suporte técnico prioritário para iniciantes"
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 bg-white/5 p-4 rounded-xl border border-white/10">
                      <CheckCircle2 className="w-5 h-5 text-electric" />
                      <span className="font-bold text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative">
                <div className="aspect-video rounded-[3rem] bg-gradient-to-br from-electric/20 to-transparent border border-white/20 p-8 flex flex-col justify-center text-center backdrop-blur-sm">
                  <div className="text-4xl font-black mb-2 tracking-tighter italic uppercase text-electric">Primeira Venda em 24h</div>
                  <p className="text-white/60 text-sm">Seguindo nosso passo a passo, a média de tempo para a primeira conversão de um iniciante P2 PRO é de apenas um dia útil.</p>
                </div>
              </div>
            </div>
          )
        }
      ]}
      benefits={[
        { title: "Risco Zero de Estoque", description: "Use o sistema pós-pago e pague apenas quando o seu cliente pagar você." },
        { title: "Apps Intuitivos", description: "Nossos aplicativos são tão simples de usar quanto a Netflix." },
        { title: "Liberdade de Tempo", description: "Comece nas horas vagas e escale conforme se sentir seguro." }
      ]}
      howItWorks={[
        { title: "Login", description: "Acesse seu painel administrativo liberado na hora." },
        { title: "Estudo", description: "Veja os tutoriais rápidos na nossa área de membros." },
        { title: "Teste", description: "Mande um teste para um amigo ou parente agora mesmo." },
        { title: "Lucro", description: "Sinta o prazer da sua primeira comissão 100% livre." }
      ]}
      diferenciais={[
        { title: "Infraestrutura Elite", description: "P2PRO é sinônimo de sinal que não cai, ideal para quem está aprendendo." },
        { title: "Suporte Consultivo", description: "Não apenas 'arrumamos' problemas, ensinamos você a evitá-los." },
        { title: "Marketing Pronto", description: "Receba imagens e vídeos prontos para postar no seu status e atrair clientes." }
      ]}
      faq={[
        { question: "Quanto tempo preciso estudar?", answer: "Em 30 minutos assistindo nossos tutoriais você já estará apto a realizar ativações profissionais." },
        { question: "O sinal trava em dias de jogos?", answer: "Não! Utilizamos balanceamento de carga para garantir estabilidade mesmo em finais de campeonato." },
        { question: "Como recebo dos meus clientes?", answer: "O cliente paga direto para você via Pix ou link de pagamento. O lucro é imediato." }
      ]}
      breadcrumbs={[
        { label: "Revenda IPTV", href: "/revenda-iptv" },
        { label: "Iniciantes", href: "/revenda-iptv/iniciantes" }
      ]}
      relatedArticles={[
        { title: "Quanto Ganha um Revendedor", href: "/revenda-iptv/quanto-ganha" },
        { title: "Como Ser um Revendedor", href: "/revenda-iptv/como-ser" },
        { title: "Planos de Revenda", href: "/revenda-iptv/plano" }
      ]}
    />
  ),
});
